/**
 * @fileoverview Job Shipment Tracking Hook
 * 
 * Manages shipment tracking and delivery logistics for awarded jobs. Vendors
 * create shipments to track physical delivery of products to client locations.
 * Supports multiple shipments per job for staged or split deliveries.
 * 
 * Shipment Lifecycle:
 * 1. pending: Shipment created but not yet shipped
 * 2. in_transit: Package shipped and en route
 * 3. out_for_delivery: Package on delivery vehicle
 * 4. delivered: Successfully delivered
 * 5. delivery_attempted: Delivery attempt failed
 * 6. exception: Issue requiring attention (damaged, lost, etc.)
 * 7. returned: Package returned to sender
 * 
 * Shipment Structure:
 * - Each shipment linked to a delivery location from job_delivery_details
 * - Multiple tracking numbers supported per shipment (multi-package)
 * - Carrier information and estimated delivery dates tracked
 * - Status notes for exceptions and special handling
 * 
 * Delivery Locations:
 * Jobs can have multiple delivery locations for:
 * - Staged rollouts to multiple sites
 * - Partial deliveries to different facilities
 * - Drop shipments to end customers
 * 
 * Common Use Cases:
 * - Vendor ships completed products to client
 * - Partial shipments for large orders
 * - Multiple deliveries to different client locations
 * - Tracking delivery progress and exceptions
 * - Updating delivery status in real-time
 * 
 * Key Features:
 * - Create and track multiple shipments per job
 * - Link shipments to specific delivery locations
 * - Multiple tracking numbers per shipment
 * - Status updates with notes
 * - Toast notifications for feedback
 * - Auto-refresh after mutations
 * 
 * @module useJobShipments
 */

import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { JobShipment, CreateShipmentData } from '@/lib/types/job';
import { useToast } from '@/hooks/use-toast';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

/**
 * Hook for managing job shipments and delivery tracking
 * 
 * Provides complete shipment lifecycle management for vendors to track product
 * delivery to clients. Handles shipment creation, updates, status tracking,
 * and deletion with automatic refresh and user feedback.
 * 
 * Data Fetching:
 * - Shipments fetched on mount and after mutations
 * - Delivery locations fetched separately
 * - Results ordered by creation date (most recent first)
 * 
 * Toast Notifications:
 * - Success messages for create/update/delete operations
 * - Error messages with detailed failure reasons
 * - Consistent user feedback across all operations
 * 
 * @param jobId - The UUID of the job to manage shipments for
 * @returns Object with shipment data, delivery locations, and management functions
 * 
 * @example
 * ```tsx
 * function ShipmentTracker({ jobId }) {
 *   const {
 *     shipments,
 *     deliveryLocations,
 *     loading,
 *     createShipment,
 *     updateShipment
 *   } = useJobShipments(jobId);
 * 
 *   const handleCreateShipment = async () => {
 *     await createShipment({
 *       delivery_location_id: locations[0].id,
 *       carrier_name: 'FedEx',
 *       tracking_numbers: ['1234567890'],
 *       ship_date: new Date(),
 *       estimated_delivery_date: addDays(new Date(), 3),
 *       shipment_status: 'in_transit',
 *       status_notes: 'Package picked up and in transit'
 *     });
 *   };
 * 
 *   if (loading) return <Spinner />;
 * 
 *   return (
 *     <div>
 *       <Button onClick={handleCreateShipment}>Create Shipment</Button>
 *       <ShipmentList shipments={shipments} />
 *     </div>
 *   );
 * }
 * ```
 * 
 * @example
 * ```tsx
 * // Update shipment status
 * function ShipmentStatusUpdate({ shipment }) {
 *   const { updateShipment } = useJobShipments(shipment.job_id);
 * 
 *   const markDelivered = async () => {
 *     await updateShipment(shipment.id, {
 *       shipment_status: 'delivered',
 *       actual_delivery_date: new Date(),
 *       status_notes: 'Delivered to receiving dock, signed by J. Smith'
 *     });
 *   };
 * 
 *   return (
 *     <Button onClick={markDelivered}>Mark as Delivered</Button>
 *   );
 * }
 * ```
 */
export const useJobShipments = (jobId: string) => {
  const [shipments, setShipments] = useState<JobShipment[]>([]);
  const [deliveryLocations, setDeliveryLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  /**
   * Fetches all shipments for the job
   * 
   * Queries job_shipments table ordered by creation date descending.
   * Shows most recent shipments first for better UX.
   */
  const fetchShipments = async () => {
    try {
      const { data, error } = await supabase
        .from('job_shipments')
        .select('*')
        .eq('job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setShipments((data || []) as JobShipment[]);
    } catch (error) {
      console.error('Error fetching shipments:', error);
      toast({
        title: 'Error',
        description: 'Failed to load shipments',
        variant: 'destructive',
      });
    }
  };

  /**
   * Fetches delivery locations configured for the job
   * 
   * Delivery locations are configured during job creation and define where
   * products should be delivered. Ordered by delivery_order for multi-location jobs.
   */
  const fetchDeliveryLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('job_delivery_details')
        .select('*')
        .eq('job_id', jobId)
        .order('delivery_order');

      if (error) throw error;
      setDeliveryLocations(data || []);
    } catch (error) {
      console.error('Error fetching delivery locations:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Creates a new shipment for the job
   * 
   * Vendors create shipments when they ship products to clients. Shipment
   * includes carrier information, tracking numbers, and estimated delivery date.
   * 
   * Creation Process:
   * 1. Authenticate user
   * 2. Get job's organization ID
   * 3. Insert shipment record
   * 4. Show success toast
   * 5. Refresh shipment list
   * 
   * Required Fields:
   * - delivery_location_id: Where to deliver
   * - tracking_numbers: Array of carrier tracking numbers
   * - shipment_status: Current status
   * 
   * Optional Fields:
   * - carrier_name: Shipping carrier (FedEx, UPS, etc.)
   * - ship_date: When package was shipped
   * - estimated_delivery_date: Expected delivery date
   * - status_notes: Additional information
   * 
   * @param shipmentData - Shipment details
   * @throws Error if creation fails
   * 
   * @example
   * ```typescript
   * await createShipment({
   *   delivery_location_id: 'location-uuid',
   *   carrier_name: 'UPS',
   *   tracking_numbers: ['1Z999AA10123456784', '1Z999AA10123456785'],
   *   ship_date: new Date('2024-01-15'),
   *   estimated_delivery_date: new Date('2024-01-18'),
   *   shipment_status: 'in_transit',
   *   status_notes: 'Two packages, both in transit'
   * });
   * ```
   */
  const createShipment = async (shipmentData: CreateShipmentData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: job } = await supabase
        .from('jobs')
        .select('organization_id')
        .eq('id', jobId)
        .single();

      if (!job) throw new Error('Job not found');

      const { data: newShipment, error } = await supabase.from('job_shipments').insert({
        job_id: jobId,
        organization_id: job.organization_id,
        delivery_location_id: shipmentData.delivery_location_id || null,
        carrier_name: shipmentData.carrier_name,
        tracking_numbers: shipmentData.tracking_numbers,
        ship_date: shipmentData.ship_date?.toISOString(),
        estimated_delivery_date: shipmentData.estimated_delivery_date?.toISOString(),
        shipment_status: shipmentData.shipment_status,
        status_notes: shipmentData.status_notes,
        created_by_user_id: user.id,
        last_updated_by_user_id: user.id,
      }).select('id').single();

      if (error) throw error;

      // Send notification to client about the new shipment
      if (newShipment?.id) {
        try {
          await invokeEdgeFunction('shipment-notify', {
            body: {
              shipmentId: newShipment.id,
              jobId: jobId,
            },
            callerOperation: 'useJobShipments.createShipment',
          });
          console.log('Shipment notification sent to client');
        } catch (notifError) {
          console.error('Failed to send shipment notification:', notifError);
          // Non-fatal - don't fail the shipment creation
        }
      }

      // Track shipment creation
      analytics.track({
        name: ANALYTICS_EVENTS.SHIPMENT_ADDED,
        category: 'engagement',
        properties: {
          job_id: jobId,
          carrier: shipmentData.carrier_name,
          tracking_count: shipmentData.tracking_numbers?.length || 0,
        },
      });

      toast({
        title: 'Success',
        description: 'Shipment created successfully',
      });

      await fetchShipments();
    } catch (error: any) {
      console.error('Error creating shipment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create shipment',
        variant: 'destructive',
      });
      throw error;
    }
  };

  /**
   * Updates an existing shipment
   * 
   * Vendors update shipments to reflect current status, add tracking information,
   * or correct details. Common updates include status changes and actual delivery dates.
   * 
   * Update Process:
   * 1. Authenticate user
   * 2. Prepare update data with date conversions
   * 3. Update shipment record
   * 4. Show success toast
   * 5. Refresh shipment list
   * 
   * Common Update Scenarios:
   * - Status change: in_transit → delivered
   * - Add actual delivery date when delivered
   * - Update tracking numbers if changed
   * - Add status notes for exceptions
   * 
   * @param shipmentId - ID of shipment to update
   * @param updates - Partial shipment data to update
   * @throws Error if update fails
   * 
   * @example
   * ```typescript
   * // Mark as delivered
   * await updateShipment(shipmentId, {
   *   shipment_status: 'delivered',
   *   actual_delivery_date: new Date(),
   *   status_notes: 'Delivered successfully, signed by recipient'
   * });
   * 
   * // Update tracking information
   * await updateShipment(shipmentId, {
   *   tracking_numbers: ['new-tracking-123'],
   *   status_notes: 'Corrected tracking number'
   * });
   * ```
   */
  const updateShipment = async (shipmentId: string, updates: Partial<CreateShipmentData>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const updateData: any = {
        ...updates,
        last_updated_by_user_id: user.id,
      };

      // Handle empty delivery_location_id (for "Other" option)
      if ('delivery_location_id' in updates) {
        updateData.delivery_location_id = updates.delivery_location_id || null;
      }

      // Convert Date objects to ISO strings
      if (updates.ship_date) {
        updateData.ship_date = updates.ship_date.toISOString();
      }
      if (updates.estimated_delivery_date) {
        updateData.estimated_delivery_date = updates.estimated_delivery_date.toISOString();
      }

      const { error } = await supabase
        .from('job_shipments')
        .update(updateData)
        .eq('id', shipmentId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Shipment updated successfully',
      });

      await fetchShipments();
    } catch (error: any) {
      console.error('Error updating shipment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update shipment',
        variant: 'destructive',
      });
      throw error;
    }
  };

  /**
   * Deletes a shipment
   * 
   * Permanently removes shipment from database. Use cautiously as this cannot
   * be undone. Typically used for incorrect or duplicate shipments.
   * 
   * @param shipmentId - ID of shipment to delete
   * @throws Error if deletion fails
   * 
   * @example
   * ```typescript
   * // Delete incorrect shipment
   * if (confirm('Are you sure you want to delete this shipment?')) {
   *   await deleteShipment(shipmentId);
   * }
   * ```
   */
  const deleteShipment = async (shipmentId: string) => {
    try {
      const { error } = await supabase
        .from('job_shipments')
        .delete()
        .eq('id', shipmentId);

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Shipment deleted successfully',
      });

      await fetchShipments();
    } catch (error: any) {
      console.error('Error deleting shipment:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete shipment',
        variant: 'destructive',
      });
      throw error;
    }
  };

  useEffect(() => {
    if (jobId) {
      fetchShipments();
      fetchDeliveryLocations();
    }
  }, [jobId]);

  return {
    shipments,
    deliveryLocations,
    loading,
    createShipment,
    updateShipment,
    deleteShipment,
    refetch: fetchShipments,
  };
};